<script>
  /** Main.svelte
   *
   */

  import { onDestroy, tick } from 'svelte';
  import { fade, scale } from 'svelte/transition';
  import io from 'socket.io-client/dist/socket.io.js';
  import Peer from 'peerjs';
  import config from '../config.js';
  import validateBrowser from '../helpers/validate-browser';
  import { getLocalStream, getRandomStartCoord } from '../helpers/utils';
  import {
    apiServer,
    peerServerHost,
    peerServerPort,
    peerServerSecure,
  } from '../helpers/host-sniffer.js';
  import { token, peers, ui } from '../stores';
  import {
    Cloud,
    Entrance,
    Feedback,
    Floor,
    GetRoom,
    Header,
    Person,
    Token,
    Toolbar,
  } from '../components';

  export let roomFromUrl = null;

  let socket, updatedTokenPosition, pendingUpdatedClusterIds;

  $: theme = $ui.theme || $ui.defaultTheme;

  // $: console.log('$: $peers:', $peers);
  // $: {
  //   let firstPeer = Object.values($peers)[0] || {};
  //   console.log('firstPeer.clusterId:', firstPeer.clusterId);
  // }

  // Reject bad browsers
  const validBrowser = validateBrowser();
  if (!validBrowser) window.location = '/bad-browser';

  // Skip entrance for debug?
  if (config.debug.on && config.debug.skipEntrance) skipEntrance();
  async function skipEntrance() {
    ui.setTheme('public');
    $token.snapshot = '000';
    $token.stream = await getLocalStream(config.mediaConstraints);
    signIn();
  }

  // Snowpack: support Hot Module Replacement
  // onDestroy(async () => {
  // pio.signOut();
  // });

  // Sign in
  async function signIn() {
    console.log('signing in');
    // Do we have required snapshot and stream?
    if (!$ui.roomName || !$token.snapshot || !$token.stream) {
      console.error('Tried to sign in without required roomName, snapshot or stream');
      return;
    }

    // 1. Get random coordinates within 25% x,y of center of floor
    $token.x = getRandomStartCoord(20);
    $token.y = getRandomStartCoord(20);

    // 2. Get a socket connection.
    socket = io(apiServer, { withCredentials: true });

    socket.on('connect', () => {
      $token.id = socket.id;
      console.log('Got peerId:', $token.id);

      // 3. Listen for events.
      socket.on('initPeers', (initPeers) => {
        console.log('initPeers:', initPeers);
        $peers = initPeers;
        ui.startChatter();
        joinOrLeaveClusterBasedOnProximity();
        token.updateClusterBoundaries($peers);
      });

      socket.on('addPeer', (peer) => {
        console.log('addPeer:', peer);
        $peers = { ...$peers, [peer.id]: peer };
        joinOrLeaveClusterBasedOnProximity();
        token.updateClusterBoundaries($peers);
      });

      socket.on('updatePositions', (updates) => {
        delete updates[socket.id];
        peers.mergeUpdates(updates);
        joinOrLeaveClusterBasedOnProximity();
        token.updateClusterBoundaries($peers);
      });

      socket.on('updateClusterIds', (updates) => {
        console.log('updateClusterIds:', updates);
        const myUpdate = updates[socket.id] || {};
        delete updates[socket.id];
        peers.mergeUpdates(updates);
        if (myUpdate.clusterId === null) {
          $token.clusterId = null;
          $ui.zoomLevel = 0;
        } else if (
          myUpdate.clusterId &&
          peers.getClusterSize(myUpdate.clusterId) < config.maxClusterSize
        ) {
          $token.clusterId = myUpdate.clusterId;
          $ui.zoomLevel = 1;
        }
        token.updateClusterBoundaries($peers);
        updateStreams();
        pendingUpdatedClusterIds = false;
      });

      socket.on('reducePeers', (remainingPeerIds) => {
        const reducedPeers = {};
        remainingPeerIds = remainingPeerIds.filter((peerId) => peerId != socket.id);
        remainingPeerIds.forEach((peerId) => {
          reducedPeers[peerId] = $peers[peerId];
        });
        $peers = reducedPeers;
        joinOrLeaveClusterBasedOnProximity();
        token.updateClusterBoundaries($peers);
      });

      // 4. Emit latest token position every 300ms.
      setInterval(() => {
        if (updatedTokenPosition) {
          socket.emit('updatePosition', updatedTokenPosition);
          updatedTokenPosition = null;
        }
      }, 1000);

      // 5. Instantiate a `peerjs` object for calling / answering calls.
      peerjs = new Peer($token.id, {
        host: peerServerHost,
        port: peerServerPort,
        secure: peerServerSecure, // Todo: Add iceServers option
      });

      // Listen for incoming calls.
      peerjs.on('call', (call) => {
        console.log('Received call from:', call.peer.slice(-3));
        call.answer($token.stream);
        call.on('stream', (stream) => {
          console.log('Answered call. Got stream from:', call.peer.slice(-3));
          $peers[call.peer].stream = stream;
        });
      });

      socket.emit('signIn', {
        roomName: $ui.roomName,
        snapshot: $token.snapshot,
        x: $token.x,
        y: $token.y,
      });
    });
  }

  function joinOrLeaveClusterBasedOnProximity() {
    // console.log('joinOrLeaveClusterBasedOnProximity was called.');
    if (pendingUpdatedClusterIds) return;

    // No longer any clustered peers within range?
    if ($token.clusterId && !token.isStillInCluster($peers)) {
      // console.log('leaveCluster -- was in:', $token.clusterId);
      socket.emit('leaveCluster');
      pendingUpdatedClusterIds = true;
      return;
    }
    // Joining a new cluster?
    const nearestInRangePeerId = token.getNearestInRangePeerId($peers);
    // console.log('nearestInRangePeerId:', nearestInRangePeerId);
    if (!$token.clusterId && nearestInRangePeerId) {
      // console.log('joining cluster with:', nearestInRangePeerId);
      socket.emit('joinCluster', nearestInRangePeerId);
      pendingUpdatedClusterIds = true;
    }
  }

  function tokenMoved(e) {
    const { x, y } = e.detail;
    updatedTokenPosition = { x, y }; // ex: { x: 50, y: 50 }
    joinOrLeaveClusterBasedOnProximity();
  }

  function updateStreams() {
    // This should only be called when self.clusterId has an id.
    if (!$token.clusterId) return;

    // Our `peers` object and peer.clusterId tells us whether we should be connected or not.
    // So whenever we get updatePeers, we should set stream or call as needed.

    for (let peerId in $peers) {
      const peer = $peers[peerId];
      if (peer.clusterId == $token.clusterId) {
        enableStream(peerId);
      } else {
        disableStream(peerId);
      }
    }
  }

  function enableStream(peerId) {
    const peer = $peers[peerId];
    if (!peer) return;

    // If we already had a stream, make that it's enabled.
    const stream = peer.stream instanceof MediaStream ? peer.stream : false;
    if (stream) {
      // console.log('Existing stream found:', stream);
      stream.getTracks().forEach((t) => (t.enabled = true));
      return;
    }

    // Otherwise, we need to call this peer if our ID is higher (otherwise just wait for his call).
    // console.log('NO existing stream');
    if ($token.id > peer.id) {
      // console.log("Since $token.id is greater, I'll call:", peer.id);
      console.log('Calling:', peerId.slice(-3));
      const call = peerjs.call(peer.id, $token.stream);

      // Then set the stream when it arrives.
      call &&
        call.on('stream', (stream) => {
          console.log('Got stream from:', peerId.slice(-3));
          $peers[peerId].stream = stream;
        });
    }
  }

  function disableStream(peerId) {
    // Disable audio/video tracks for this peer.
    const stream = $peers[peerId] && $peers[peerId].stream;

    // No stream to disable? No problem. Our work is done.
    if (!stream || !stream instanceof MediaStream) return;

    // Okay disable the tracks.
    stream.getTracks().forEach((t) => (t.enabled = false));
  }

  function handleKeys(e) {
    // Spacebar? Toggle zoomLevel.
    if (e.keyCode == 32) {
      e.preventDefault();
      // If in a cluster, toggle between full zoom in and full zoom out.
      if ($token.clusterId) {
        $ui.zoomLevel *= -1;
      }
      // Otherwise, toggle between default zoom and full zoom out.
      else {
        $ui.zoomLevel = $ui.zoomLevel == 0 ? -1 : 0;
      }
    }
  }
</script>

<svelte:window on:keydown="{handleKeys}" />

<svelte:head>
  <title>MingleSpace</title>
</svelte:head>

<Header />
{#if validBrowser}
  <main
    class="flex relative overflow-hidden z-20"
    on:mouseover="{() => ($ui.toolbarIsHovered = false)}">
    <Feedback feedbackMessage="{$ui.feedbackMessage}" />

    <!-- Main Floor -->
    <Floor>
      <!-- roomName not set and not included with URL, need to ask for it. -->
      {#if !$ui.roomName && !roomFromUrl}
        <h2>{roomFromUrl}</h2>
        <GetRoom />
      {/if}

      <!-- If roomName is included with URL, browser requires a user click in order to play video -->
      {#if !$ui.roomName && roomFromUrl}
        <div class="flex w-full h-full items-center justify-center">
          <button
            class="button w-auto py-6 px-8"
            style="background: {theme.buttonPrimary.background}; color: {theme.buttonPrimary.text}"
            on:click="{() => ui.setTheme(roomFromUrl)}"
            transition:fade>
            Enter
            {theme.name}
          </button>
        </div>
      {/if}

      <!-- Entrance -->
      {#if $ui.roomName && !$token.stream && !config.debug.on}
        <Entrance on:signIn="{signIn}" />
      {/if}

      <!-- Main -->
      {#if $ui.theme && $token.stream}
        <Token on:tokenMoved="{tokenMoved}" />
        {#if $token.clusterId}
          <Cloud x="{$token.x}" y="{$token.y}" />
        {/if}
        {#each Object.values($peers) as peer (peer.id)}
          <Person peer="{peer}" />
          {#if peer.clusterId}
            <Cloud x="{peer.x}" y="{peer.y}" />
          {/if}
        {/each}
      {/if}
    </Floor>
  </main>

  <!-- Toolbar -->
  <Toolbar />
{/if}
